import { PureComponent } from 'react';
import debug from 'debug';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';

const logHeartBeat = debug('core:player:components:heartbeat');
const logHeartBeatCheckInterval = debug('core:player:components:heartbeat:check-interval');
const logHeartBeatPhoenix = debug('core:player:components:heartbeat:phoenix');
const logPlayer = debug('core:modules:player:container:container');

class PlayerContainer extends PureComponent {
  static propTypes = {
    children: PropTypes.node,
    videoBitRateChange: PropTypes.func.isRequired,
    heartBeat: PropTypes.func.isRequired,
    onHeartBeat: PropTypes.func,
    playerInstance: PropTypes.shape({}),
    playerEventsList: PropTypes.func.isRequired,
  };

  static defaultProps = {
    children: null,
    playerInstance: null,
    onHeartBeat: null,
  };

  componentDidMount() {
    const { playerInstance } = this.props;
    if (playerInstance) {
      this.subscribeEvents(playerInstance);
    }
  }

  componentDidUpdate(prevProps) {
    const { playerInstance } = this.props;
    if (playerInstance && playerInstance !== prevProps.playerInstance) {
      this.subscribeEvents(playerInstance);
    }
  }

  /*
    ++ Note on Heartbeat
    The event occur as frequently as 10 times per second so we need can't rely only on timers,
    by using the time event from JWPlayer (https://developer.jwplayer.com/jw-player/docs/javascript-api-reference/#jwplayerontime)

    The heartBeat method is throttled to be executed once every 1000ms,
    but the real interval between executions is not precise,
    to fix this issue we store the lastHeartBeatTimestamp and
    use it value to calculate the real playback time.

    The heartbeat beacon was implemented here to dispatch every 20s of playbackTime,
    with 500ms threshold to avoid skipping heartbeat.
  */
  playbackTime = 0; // in ms
  lastHeartBeatTimestamp = 0; // in ms
  pausedTimestamp = 0; // in ms
  heartBeatInterval = 1000; // in ms
  playerHeartBeatEventInterval = 20 * 1000; // 20s in ms
  acceptableHeartBeatEventThreshold = 500; // in ms
  lastHeartBeatEventTime = 0; // in s

  toSeconds = (value) => Math.round(value / 1000);
  calculateHeartBeatDiff = () => {
    const { lastHeartBeatTimestamp, pausedTimestamp } = this;
    const currentTimestamp = Date.now();

    this.pausedTimestamp = 0;
    this.lastHeartBeatTimestamp = currentTimestamp;

    let diff = 0;
    if (lastHeartBeatTimestamp > 0) {
      if (pausedTimestamp > 0) {
        diff = currentTimestamp - (pausedTimestamp - lastHeartBeatTimestamp);
      } else {
        diff = currentTimestamp - lastHeartBeatTimestamp;
      }
      if (diff >= 2 * this.heartBeatInterval) {
        diff = 0;
      }
    }
    return diff;
  };

  /*
    Check if heartbeat time need to be dispatched
    time: int (in ms)
    interval: int (in ms)
    convertedTime: int (time converted to the unit saved on lastHeartBeatEventTime)
    lastHeartBeatEventTime: int (last time the heartbeat was dispatched)
  */
  checkHeartBeat = (time, interval, convertedTime, lastHeartBeatEventTime = 0) => {
    const intervalDiff = time % interval;
    logHeartBeatCheckInterval('Checking heartbeat interval', {
      time,
      interval,
      convertedTime,
      lastHeartBeatEventTime,
      intervalDiff,
    });
    return (
      time > 0 &&
      lastHeartBeatEventTime < convertedTime &&
      intervalDiff <= this.acceptableHeartBeatEventThreshold
    );
  };

  dispatchPhoenixHeartbeat = () => {
    const timeInSeconds = this.toSeconds(this.playbackTime);
    const sendAction = this.checkHeartBeat(
      this.playbackTime,
      this.playerHeartBeatEventInterval,
      timeInSeconds,
      this.lastHeartBeatEventTime,
    );
    if (sendAction) {
      logHeartBeatPhoenix(
        `Sending heartbeat action with ${timeInSeconds} seconds of content played`,
      );
      this.props.heartBeat(timeInSeconds); // Player heartbeat action (beacon))
      // lastHeartBeatEventTime is used to avoid sending two events on the same second
      this.lastHeartBeatEventTime = timeInSeconds;
    }
  };

  heartBeat = () => {
    const diff = this.calculateHeartBeatDiff();

    this.playbackTime += diff;

    logHeartBeat('Heartbeat', { playbackTime: this.playbackTime, diff });

    this.dispatchPhoenixHeartbeat();

    if (this.props.onHeartBeat) {
      this.props.onHeartBeat({
        playbackTime: this.playbackTime,
        checkHeartBeat: this.checkHeartBeat,
        diff,
      });
    }
  };

  heartBeatThrottled = throttle(this.heartBeat, this.heartBeatInterval, { leading: true });

  checkPlayInterval;

  timeDebounced = debounce(
    (tm) => {
      if (!this.props.playerInstance.isPlaying() || !!this.checkPlayInterval) {
        return;
      }
      this.props.playerInstance.pause();
      this.checkPlayInterval = setInterval(() => {
        const isPlaying = this.props.playerInstance.isPlaying();
        if (!isPlaying) {
          this.props.playerInstance.play(true);
        } else {
          clearInterval(this.checkPlayInterval);
          this.checkPlayInterval = null;
        }
      }, 1000);
    },
    2000,
    { leading: false, trailing: true },
  );

  onPause = () => {
    this.pausedTimestamp = Date.now();
  };

  onTime = () => {
    this.heartBeat();
    this.timeDebounced();
  };

  onError = (err) => {
    logPlayer('playerInstance ERROR', err);
  };

  subscribeEvents = (playerInstance) => {
    const { playerEventsList } = this.props;

    playerInstance.off(playerEventsList.PLAYER_TIMEUPDATE, this.onTime);
    playerInstance.on(playerEventsList.PLAYER_TIMEUPDATE, this.onTime);

    playerInstance.off(playerEventsList.PLAYER_PAUSE, this.onPause);
    playerInstance.on(playerEventsList.PLAYER_PAUSE, this.onPause);

    playerInstance.off(playerEventsList.PLAYER_ERROR, this.onError);
    playerInstance.on(playerEventsList.PLAYER_ERROR, this.onError);

    playerInstance.off(playerEventsList.PLAYBACK_BITRATE, this.onVisualQualityChange);
    playerInstance.on(playerEventsList.PLAYBACK_BITRATE, this.onVisualQualityChange);
  };

  onVisualQualityChange = ({ level: { bitrate } }) => {
    this.props.videoBitRateChange({ bitrate });
  };

  render() {
    return this.props.children;
  }
}

export { PlayerContainer };
